/**
 * Copilot create-registry — entity_type / module → concrete create surface.
 *
 * RESILIENT FALLBACK for the Agent-mode executor (AgentModePanel). The 3 MAIN
 * domain pages — `risk-pro/register`, `compliance-pro/register`,
 * `governance-pro/policies` — are CUSTOM pages (not the generic CrudPage), so
 * historically they registered no copilot "create surface". When no surface is
 * mounted the executor had no create/link endpoint to hit, so `createRecord`
 * failed with "Aucun point de création disponible" and `linkRecords` with
 * "endpoint de lien introuvable".
 *
 * This mirrors frontend-client's `create-registry.getCreateEntryByEntityType`:
 * given a planner `entity_type` (or a route/module slug) it resolves the backend
 * create endpoint PLUS a `fieldMap` so a `createRecord` body matches the backend
 * contract without dropping any values:
 *   - risk       : planner `title` → backend `name`
 *   - obligation : planner `title` / `name` → backend `requirement`
 *   - policy     : planner `name` → backend `title`, inject `type: 'policy'`
 *   - control    : planner `name` → backend `title`
 *
 * The registry is intentionally self-contained (no formSchemas coupling) so it
 * can also seed a link endpoint: `${createPath}/{sourceId}/links`.
 */

export interface CreateEntry {
  /** First route segment (module slug), e.g. `risk-pro`. */
  module: string
  /** Knowledge entity type used by search / suggest-links / planner. */
  entityType: string
  /** Backend create endpoint (relative to the api client base). */
  createPath: string
  /** FR/EN keywords for free-text intent resolution. */
  keywords: string[]
  /** Human FR label. */
  title: string
  /**
   * Rename map applied to a `createRecord` body before POST: planner/schema
   * field name → backend field name (e.g. a risk's `title` → `name`). Keys NOT
   * in the map pass through unchanged, so nothing is silently dropped.
   */
  fieldMap?: Record<string, string>
  /**
   * Constant fields folded into every `createRecord` body for this entry — used
   * for backend discriminators the form doesn't surface (e.g. a normative
   * document's `type: 'policy'`).
   */
  fixedFields?: Record<string, unknown>
}

// Ordered by resolution priority (first keyword hit wins for free-text lookup).
const CREATE_ENTRIES: CreateEntry[] = [
  {
    module: 'risk-pro',
    entityType: 'risk',
    createPath: '/risk-pro/risks',
    keywords: ['risque', 'risk', 'menace'],
    title: 'Risque',
    // Backend risk column is `name`; a planner that emits `title` maps onto it.
    fieldMap: { title: 'name' },
  },
  {
    module: 'compliance-pro',
    entityType: 'obligation',
    createPath: '/compliance-pro/obligations',
    keywords: [
      'obligation', 'exigence', 'obligation réglementaire', 'obligation reglementaire',
      'exigence réglementaire', 'exigence reglementaire',
    ],
    title: 'Obligation',
    // Backend column is `requirement`; map both common title-ish aliases onto it.
    fieldMap: { title: 'requirement', name: 'requirement' },
  },
  {
    module: 'governance-pro',
    entityType: 'policy',
    createPath: '/governance-pro/normative-documents',
    keywords: ['politique', 'policy', 'normative', 'directive'],
    title: 'Politique',
    // Backend column is `title`; a normative document needs a `type` discriminator
    // and stores the domain under `domainId`.
    fieldMap: { name: 'title', domain: 'domainId' },
    fixedFields: { type: 'policy' },
  },
  {
    module: 'compliance-pro',
    entityType: 'control',
    createPath: '/compliance-pro/controls',
    keywords: ['contrôle', 'controle', 'control', 'mesure de contrôle', 'security control'],
    title: 'Contrôle',
    // Backend column is `title`; drop-in for a planner that emits `name`.
    fieldMap: { name: 'title' },
  },
  {
    module: 'security-pro',
    entityType: 'incident',
    createPath: '/security-pro/incidents',
    keywords: ['incident', 'intrusion', 'breach', 'faille'],
    title: 'Incident',
  },
  {
    module: 'vendor-pro',
    entityType: 'vendor',
    createPath: '/vendor-pro/catalogue',
    keywords: ['fournisseur', 'vendor', 'tiers', 'prestataire', 'sous-traitant'],
    title: 'Fournisseur',
    // Backend column is `name`; map a planner that emits `title`.
    fieldMap: { title: 'name' },
  },
]

function normalize(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
}

/** All registered create entries. */
export function listCreateEntries(): CreateEntry[] {
  return CREATE_ENTRIES
}

/**
 * Resolve a create entry by its knowledge `entityType` (e.g. 'policy', 'risk',
 * 'obligation'). Tolerates a few common planner aliases so a `createRecord` with
 * `entity_type='security_control'` lands on the control entry.
 */
export function getCreateEntryByEntityType(entityType?: string): CreateEntry | undefined {
  if (!entityType) return undefined
  const e = normalize(entityType)
  const direct = CREATE_ENTRIES.find((c) => normalize(c.entityType) === e)
  if (direct) return direct
  return CREATE_ENTRIES.find(
    (c) => normalize(c.module) === e || c.keywords.some((kw) => e.includes(normalize(kw))),
  )
}

/**
 * Resolve a create entry by module slug (first route segment). Matches the exact
 * module OR the module contained in the createPath so both `risk-pro` and a
 * route like `governance-pro/policies` resolve.
 */
export function getCreateEntryByModule(module?: string): CreateEntry | undefined {
  if (!module) return undefined
  const m = normalize(module)
  return CREATE_ENTRIES.find(
    (e) => normalize(e.module) === m || normalize(e.createPath).includes(m),
  )
}

/**
 * Apply an entry's `fieldMap` (rename) + `fixedFields` (constants) to a
 * `createRecord` body so it matches the backend contract without dropping any
 * planner-provided values. A no-op when `entry` is undefined.
 */
export function applyCreateFieldMap(
  entry: CreateEntry | undefined,
  body: Record<string, unknown>,
): Record<string, unknown> {
  if (!entry) return body
  const out: Record<string, unknown> = {}
  const map = entry.fieldMap ?? {}
  for (const [k, v] of Object.entries(body)) {
    const target = map[k] ?? k
    // Don't clobber an already-mapped value with an unmapped duplicate.
    if (out[target] === undefined) out[target] = v
  }
  for (const [k, v] of Object.entries(entry.fixedFields ?? {})) {
    if (out[k] === undefined) out[k] = v
  }
  return out
}

/**
 * Build the `${createPath}/{sourceId}/links` durable typed-link endpoint for a
 * source entity resolved by entity type / module, or `undefined` when neither
 * the entity type nor the module resolves an entry.
 */
export function resolveLinkEndpoint(
  opts: { entityType?: string; module?: string; sourceRef: string },
): string | undefined {
  const entry =
    getCreateEntryByEntityType(opts.entityType) ?? getCreateEntryByModule(opts.module)
  if (!entry || !opts.sourceRef) return undefined
  return `${entry.createPath.replace(/\/$/, '')}/${encodeURIComponent(opts.sourceRef)}/links`
}
